import type { QueryClient } from '@tanstack/react-query';
import { createRootRouteWithContext, Outlet } from '@tanstack/react-router';
import { Button, PageHeader, Wordmark } from 'pleach-ds';
import { lazy, Suspense, useEffect } from 'react';
import { useInShell } from '@/components/shellContext';
import { PublicError } from '@/features/public/PublicError';
import { gateRoute, useSession } from '@/lib/session';
import { useRouterClick } from '@/lib/useRouterClick';
// Both 404s' styles, in the entry chunk: a visitor on a mistyped link has
// never loaded the shell (and AppShell.css with it).
import '@/components/NotFound.css';

// The signed-in 404 needs the shell, and the shell stays out of the entry
// chunk that every visitor to the public site downloads.
const SignedInNotFoundPage = lazy(() => import('@/components/SignedInNotFound'));

export type RouterContext = { queryClient: QueryClient };

export const Route = createRootRouteWithContext<RouterContext>()({
  component: () => <Outlet />,
  notFoundComponent: NotFound,
  // The last catch, for whatever no route below handles (the /_app gate's
  // session check when the API is down, a signed-in page with no error
  // screen of its own): the styled public error page in place of the
  // router's bare "Something went wrong!". On the root route only, never
  // as the router's defaultErrorComponent, which would put this full-page
  // frame inside the site frame's <main> or inside the shell.
  errorComponent: PublicError,
});

/**
 * The 404. A signed-in chef keeps the shell (the rail or the tab bar), so
 * the way out is where it always is; anyone else gets a plain page with the
 * wordmark. Rendered inside the shell when the router puts it there (a
 * signed-in route that throws notFound, such as /admin for a non-admin), and
 * wrapped in one otherwise; never both.
 */
function NotFound() {
  const inShell = useInShell();
  const { data: session, isPending } = useSession();
  if (inShell || (!isPending && session?.authenticated && !gateRoute(session))) {
    return (
      <Suspense fallback={null}>
        <SignedInNotFoundPage wrap={!inShell} />
      </Suspense>
    );
  }
  if (isPending) return null;
  return <PublicNotFound />;
}

function PublicNotFound() {
  const go = useRouterClick();
  useEffect(() => {
    const before = document.title;
    const mine = 'Page not found · Pleach';
    document.title = mine;
    // A page that does not exist is never one to index: the same robots
    // rule useDocumentMeta writes for an unpublished page.
    const robots = document.createElement('meta');
    robots.name = 'robots';
    robots.content = 'noindex';
    document.head.appendChild(robots);
    // Put back what was there, unless the next page has already named itself.
    return () => {
      robots.remove();
      if (document.title === mine) document.title = before;
    };
  }, []);
  return (
    <div className="app-notfound--public">
      <header className="app-notfound__bar">
        <a href="/" className="app-notfound__home" aria-label="Pleach home" onClick={go('/')}>
          <Wordmark size={22} tone="ink" />
        </a>
      </header>
      <main id="main" tabIndex={-1} className="app-notfound__main">
        <PageHeader eyebrow="Error 404" title="Page not found" />
        <p className="app-notfound__body">
          That page doesn&apos;t exist, or it has moved. Check the address, or start again from the home page.
        </p>
        <div className="app-notfound__actions">
          <Button variant="primary" href="/" onClick={go('/')}>
            Go to the home page
          </Button>
          <Button variant="secondary" href="/login" onClick={go('/login')}>
            Log in
          </Button>
        </div>
      </main>
    </div>
  );
}
